import { useState } from 'react';

import { appColors } from '@backrs_ui/common-ui/src/assets/appColors';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsChip } from '@backrs_ui/common-ui/src/atoms/BackrsChip';
import { BackrsIcons } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';
import { BackrsIconRow } from '@backrs_ui/common-ui/src/atoms/BackrsIconRow';
import { FitExplainer } from '@backrs_ui/common-ui/src/atoms/FitExplainer';
import { CareerPathScoreSection } from '@backrs_ui/common-ui/src/models/careerPath';
import { NUGGET_THEMES } from '@backrs_ui/common-ui/src/models/nugget';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';
import { HandleAPICallResponseReturn } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';

import { Theme, Box, SxProps, Typography, Tooltip } from '@mui/material';

import { HandleShareParams } from './DialogNugget';

const DIALOG_NUGGET_STRINGS = appStrings.modules.nugget.dialogNuggetActions;

type DialogNuggetHeaderProps = Readonly<{
  title: string;
  titleOnClick?: () => void;
  subTitle?: string;
  columnCategory: string;
  saveToLibrary?: () => void;
  shareParams?: HandleShareParams;
  markAsDone?: () => void;
  customMenuActions?: {
    icon: BackrsIcons;
    text: string;
    onClick: () => void;
  }[];
  hasCompletedNugget?: boolean;
  hasBookmarkedNugget?: boolean;
  fit?: CareerPathScoreSection;
  fitExplainerOnClick?: () => Promise<HandleAPICallResponseReturn | undefined>;
  fitExplainerText?: string;
  user?: BackrsUser | undefined;
}>;

export async function handleShareWithNavigator(shareParams: HandleShareParams) {
  try {
    if (navigator.share) {
      const { title, text, url } = shareParams;

      await navigator.share({
        title,
        text,
        url,
      });

      if (shareParams.afterShare) {
        shareParams.afterShare();
      }
    }
  } catch (error) {
    console.log(error);
  }
}

export const DialogNuggetHeader = ({
  title,
  titleOnClick,
  subTitle,
  columnCategory,
  saveToLibrary,
  shareParams,
  markAsDone,
  customMenuActions,
  hasCompletedNugget,
  hasBookmarkedNugget,
  fit,
  fitExplainerOnClick,
  fitExplainerText,
  user,
}: DialogNuggetHeaderProps) => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const areThereMenuActions =
    saveToLibrary || shareParams || markAsDone || customMenuActions?.length;

  const handleCopyLink = async () => {
    if (shareParams) {
      try {
        await navigator.clipboard.writeText(shareParams.url);
        setShowCopySuccess(true);

        // Auto-hide tooltip after 1.5 seconds
        setTimeout(() => {
          setShowCopySuccess(false);
        }, 1500);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  return (
    <Box>
      <Box sx={containerStyle}>
        <Box sx={dialogNuggetHeaderStyle}>
          {columnCategory && (
            <Box sx={chipContainer}>
              <BackrsChip
                label={columnCategory}
                variant={{ type: 'LARGE_TEXT', backgroundColor: 'BLUE' }}
              />
            </Box>
          )}
          <Typography
            sx={{ ...titleStyle, cursor: titleOnClick ? 'pointer' : 'default' }}
            onClick={titleOnClick}
          >
            {title}
          </Typography>
          {fit && (
            <FitExplainer
              fit={fit}
              fitExplainerOnClick={fitExplainerOnClick}
              fitExplainerText={fitExplainerText}
              disabled={!user}
            />
          )}
          <Typography sx={subTitleStyle}>{subTitle}</Typography>
        </Box>
        <Box>
          {areThereMenuActions && (
            <Box sx={actionContainerStyle}>
              {!!saveToLibrary && (
                <BackrsIconRow
                  icon={hasBookmarkedNugget ? 'IconBookmarkFilled' : 'IconBookmark'}
                  text={DIALOG_NUGGET_STRINGS.bookmark}
                  onClick={saveToLibrary}
                />
              )}

              {!!shareParams && (
                <>
                  <BackrsIconRow
                    icon="IconSend"
                    text={DIALOG_NUGGET_STRINGS.share}
                    onClick={async () => await handleShareWithNavigator(shareParams)}
                  />
                  <Tooltip
                    open={showCopySuccess}
                    title={DIALOG_NUGGET_STRINGS.linkCopied}
                    placement="right"
                    arrow
                    componentsProps={{
                      popper: {
                        sx: linkCopiedPopperSx,
                      },
                    }}
                  >
                    <Box>
                      <BackrsIconRow
                        icon="IconCopy"
                        text={DIALOG_NUGGET_STRINGS.copyLink}
                        onClick={handleCopyLink}
                      />
                    </Box>
                  </Tooltip>
                </>
              )}

              {!!markAsDone && (
                <BackrsIconRow
                  icon={hasCompletedNugget ? 'IconChecked' : 'IconUnchecked'}
                  text={
                    columnCategory === NUGGET_THEMES.GROW_BY_DOING
                      ? DIALOG_NUGGET_STRINGS.markDone
                      : DIALOG_NUGGET_STRINGS.markComplete
                  }
                  onClick={markAsDone}
                />
              )}

              {customMenuActions?.map((action) => (
                <BackrsIconRow
                  key={action.text}
                  icon={action.icon}
                  text={action.text}
                  onClick={action.onClick}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Typography sx={smallScreenTitleStyle}>{title}</Typography>
      <Typography sx={smallScreenSubTitleStyle}>{subTitle}</Typography>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 2,
  display: 'flex',
  '@media (max-width: 756px)': {
    flexDirection: 'column',
    gap: 1,
  },
};

const dialogNuggetHeaderStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  mt: 3,
  minWidth: 400,
  '@media (max-width: 1000px)': {
    width: 600,
  },
  '@media (max-width: 756px)': {
    width: 100,
    minWidth: 100,
    flexDirection: 'row',
  },
};

const chipContainer: SxProps<Theme> = {
  mb: 2,
  '@media (max-width: 756px)': {
    mt: 1,
  },
};

const titleStyle: SxProps<Theme> = {
  fontFamily: 'Sarabun',
  fontSize: '32px',
  fontWeight: 700,
  minHeight: '36px',
  lineHeight: '32px',
  '@media (max-width: 756px)': {
    display: 'none',
  },
};

const smallScreenTitleStyle: SxProps<Theme> = {
  fontFamily: 'Sarabun',
  fontWeight: 700,
  minHeight: '36px',
  display: 'none',
  fontSize: '24px',
  lineHeight: '32px',
  '@media (max-width: 756px)': {
    display: 'block',
  },
};

const subTitleStyle: SxProps<Theme> = {
  fontFamily: 'Sarabun',
  fontSize: '24px',
  fontWeight: 400,
  display: 'block',
  '@media (max-width: 756px)': {
    display: 'none',
  },
};

const smallScreenSubTitleStyle: SxProps<Theme> = {
  fontFamily: 'Sarabun',
  fontSize: '20px',
  fontWeight: 400,
  display: 'none',
  '@media (max-width: 756px)': {
    display: 'block',
  },
};

const actionContainerStyle: SxProps<Theme> = {
  borderRadius: '0px 0px 4px 4px',
  px: 3,
  pt: 2,
  pb: 2,
  gap: 2,
  flexDirection: 'column',
  display: 'flex',
  minWidth: 170,
  backgroundColor: appColors.color.blue4,
  '@media (max-width: 756px)': {
    mb: 2,
  },
};

const linkCopiedPopperSx: SxProps<Theme> = {
  zIndex: 99999999999999,
};
